/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { JobContractDaysPeriodType } from '@gh/builder-shared-data';
import { FormState, FormStateBuilder, queryDirty, State, whenCreateMode } from '@gh/common-shared';
import { skip$ } from '@gh/rx/operators';
import { Subscription } from 'rxjs';
import {
  GANTT_INIT_STATE,
  ganttReducer,
  GanttState,
  GanttStateEventType,
  queryGanttLastUpdateTime,
} from '../../gantt-chart.state';
import { CalendarListItem, GanttTask } from '../../resources/gantt.model';
import { GanttChartBusService } from '../../services/gantt-chart-bus.service';
import { GanttChartCommandService } from '../../services/gantt-chart-command.service';
import { FormMode } from '@gh/core-ui';
import { trim } from 'lodash';

export function ganttStateFactory(): State<GanttState> {
  return new State(GANTT_INIT_STATE, ganttReducer);
}

@Component({
  standalone: false,
  selector: 'gh-gantt-chart',
  templateUrl: 'gantt-chart.component.html',
  styleUrls: ['gantt-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    GanttChartBusService,
    GanttChartCommandService,
    { provide: State, useFactory: ganttStateFactory },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'ghGanttChart',
})
export class GanttChartComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() editable: boolean;
  @Input() calendar: CalendarListItem[];
  @Input() multiMode: boolean = false;
  @Input() minDate: Date;
  @Input() currentDate: Date;
  @Input() scheduleTemplate?: any;
  @Input() periodType: JobContractDaysPeriodType = JobContractDaysPeriodType.WorkingDays;
  @Input() gDriveUrl?: string;
  @Input() initialRatio?: number;

  @Output() dirtyChange = new EventEmitter<boolean>();
  @Output() nameChanges = new EventEmitter<string>();
  @Output() sizeChange = new EventEmitter<string>();

  @Input()
  set tasks(tasks: GanttTask[]) {
    this._tasks = tasks;

    if (this.initialized) {
      this.state.mutate({
        type: GanttStateEventType.Merge,
        tasks: this._tasks,
      });
    }
  }

  get tasks(): GanttTask[] {
    return this._tasks;
  }
  form: FormState<any>;

  private sizeChangeTimer: any;
  private historyTimer: any;
  private historyIdleHandle: number | null = null;
  private historyPending = false;
  private initialized = false;
  private _tasks: GanttTask[];
  private dirtySubscription: Subscription;
  private historySubscription: Subscription;

  constructor(public api: GanttChartCommandService,
              private state: State<GanttState>,
              private formStateBuilder: FormStateBuilder,
              private detectChanges: ChangeDetectorRef,
              private bus: GanttChartBusService,
              private ngZone: NgZone) {
  }

  ngOnInit(): void {
    this.state.mutate({
      type: GanttStateEventType.Load,
      tasks: this._tasks,
      calendar: this.calendar,
      editable: this.editable,
      multiMode: this.multiMode,
      periodType: this.periodType,
      minDate: this.minDate,
      currentDate: this.currentDate,
    });
    this.api.registerHistoryGate(() => this.flushPendingHistoryCapture());
    this.historySubscription = this.state.select(queryGanttLastUpdateTime).pipe(
      skip$(1)).subscribe(() => {
      this.scheduleHistoryCapture();
    });
    this.initialized = true;
    this.dirtySubscription = this.state.select(queryDirty).pipe(
      skip$(1))
      .subscribe((dirty) => this.dirtyChange.emit(dirty));
    this.form = this.formStateBuilder.buildPlain({
                                                   mode: FormMode.CreateMode,
                                                   enabled: whenCreateMode(),
                                                   value: {
                                                     name: this.scheduleTemplate ? this.scheduleTemplate.name : '',
                                                   },
                                                   children: {
                                                     name: { required: !!this.scheduleTemplate, enabled: this.editable} ,
                                                   },
                                                 });
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.api.expandAllNotCompleted();
      this.api.initHistory();
      this.api.refreshTreeFilter();
    });
  }

  ngOnDestroy(): void {
    this.api.registerHistoryGate(null);
    this.cancelHistoryCapture();
    this.dirtySubscription.unsubscribe();
    this.historySubscription.unsubscribe();
    if (this.sizeChangeTimer) {
      clearTimeout(this.sizeChangeTimer);
    }
  }

  onResizerPositionChange(filter: any): void {
    const debounce = 1000;
    clearTimeout(this.sizeChangeTimer);
    this.sizeChangeTimer = setTimeout(() => {
      this.sizeChange.emit(filter);
    },                      debounce);
    if (this.bus.initialized) {
      this.bus.resize();
    }
  }

  private scheduleHistoryCapture(): void {
    this.cancelHistoryCapture();
    this.historyPending = true;
    this.historyTimer = setTimeout(() => {
      this.historyTimer = null;
      const runCapture = () => {
        this.historyIdleHandle = null;
        this.commitHistoryCapture();
      };
      const idleWindow = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      };
      if (typeof idleWindow.requestIdleCallback === 'function') {
        this.historyIdleHandle = idleWindow.requestIdleCallback(runCapture, { timeout: 1000 });
      } else {
        this.historyTimer = setTimeout(runCapture, 0);
      }
    }, 500);
  }

  private flushPendingHistoryCapture(): void {
    if (!this.historyPending) {
      return;
    }
    this.cancelHistoryCapture();
    this.commitHistoryCapture();
  }

  private commitHistoryCapture(): void {
    this.historyPending = false;
    this.state.mutate({
      type: GanttStateEventType.AddToHistory,
    });
  }

  private cancelHistoryCapture(): void {
    if (this.historyTimer) {
      clearTimeout(this.historyTimer);
      this.historyTimer = null;
    }
    if (this.historyIdleHandle != null) {
      const idleWindow = window as Window & {
        cancelIdleCallback?: (id: number) => void;
      };
      if (typeof idleWindow.cancelIdleCallback === 'function') {
        idleWindow.cancelIdleCallback(this.historyIdleHandle);
      }
      this.historyIdleHandle = null;
    }
  }

  onExpandAll(): void {
    this.api.expandAll();
  }

  onCollapseAll(): void {
    this.api.collapseAll();
  }

  onSearch(event: string): void {
    const query = (event || '').trim();
    if (query.length < 3 && !this.bus.searchString) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.api.applySearchFilter(event || '');
    });
  }

  refreshTimeLine(): void {
    this.bus.refreshTimelineVisibility();
  }
  onNameChange(event: any): void {
    const value = trim(event.target.value);
    this.nameChanges.emit(value);
    this.form.getControl('name').setValue(value);
  }
}
